import React from "react";

interface GuestContactFields {
  name: string;
  email: string;
  phone: string;
  errors: { name: string; email: string; phone: string };
  onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEmailBlur?: () => void;
  onPhoneChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

interface TicketFormValues {
  subject: string;
  description: string;
  priority: "low" | "medium" | "high" | "urgent";
  [key: string]: unknown;
}

interface TicketFormProps<T extends TicketFormValues> {
  ticketForm: T;
  setTicketForm: (form: T) => void;
  departments?: string[];
  selectedDepartment?: string;
  onDepartmentChange?: (dept: string) => void;
  showDepartmentSelect?: boolean;
  guestFields?: GuestContactFields;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel?: string;
  intro?: string;
}

const inputClass = (hasError?: boolean) =>
  `w-full border ${hasError ? "border-red-500" : "border-gray-300"} rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500`;

export function TicketForm<T extends TicketFormValues>({
  ticketForm,
  setTicketForm,
  departments = [],
  selectedDepartment,
  onDepartmentChange,
  showDepartmentSelect,
  guestFields,
  onSubmit,
  submitLabel = "Create Ticket",
  intro = "No support staff is currently online. Please create a ticket and we'll get back to you soon.",
}: TicketFormProps<T>) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 max-h-[400px] overflow-y-auto pb-4">
      <p className="text-sm text-gray-600 mb-4">{intro}</p>

      {guestFields && (
        <>
          <div className="space-y-1">
            <input
              type="text"
              placeholder="Name"
              required
              value={guestFields.name}
              onChange={guestFields.onNameChange}
              className={inputClass(!!guestFields.errors.name)}
            />
            {guestFields.errors.name && (
              <p className="text-red-500 text-sm">{guestFields.errors.name}</p>
            )}
          </div>

          <div className="space-y-1">
            <input
              type="email"
              placeholder="Email"
              required
              value={guestFields.email}
              onChange={guestFields.onEmailChange}
              onBlur={guestFields.onEmailBlur}
              className={inputClass(!!guestFields.errors.email)}
            />
            {guestFields.errors.email && (
              <p className="text-red-500 text-sm">{guestFields.errors.email}</p>
            )}
          </div>

          <div className="space-y-1">
            <input
              type="tel"
              placeholder="Phone (e.g. +61 ### ### ###)"
              value={guestFields.phone}
              onChange={guestFields.onPhoneChange}
              pattern="[0-9+() -]*"
              maxLength={20}
              className={inputClass(!!guestFields.errors.phone)}
            />
            {guestFields.errors.phone && (
              <p className="text-red-500 text-sm">{guestFields.errors.phone}</p>
            )}
          </div>
        </>
      )}

      {showDepartmentSelect && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Department</label>
          <select
            required
            value={selectedDepartment}
            onChange={(e) => onDepartmentChange?.(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="">Select Department</option>
            {departments.map((dept) => (
              <option key={dept} value={dept}>
                {dept}
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700">Subject</label>
        <input
          type="text"
          required
          value={ticketForm.subject}
          onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea
          required
          value={ticketForm.description}
          onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
          rows={4}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Priority</label>
        <select
          value={ticketForm.priority}
          onChange={(e) =>
            setTicketForm({ ...ticketForm, priority: e.target.value as TicketFormValues["priority"] })
          }
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
        >
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
      </div>

      <button
        type="submit"
        className="w-full bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-300"
      >
        {submitLabel}
      </button>
    </form>
  );
}
